import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Kevlar2Service } from '@app/features/kevlar2-validation/kevlar2-validation.service';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { ErrorCardComponent } from '../errors/components/error-card/error-card.component';
import { AvailableIndexes } from '@app/shared/models/kevlar2/available-indexes.model';
import { MessageDialogComponent } from '@app/shared/dialogs/message-dialog/message-dialog.component';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-kevlar2-validation',
    templateUrl: './kevlar2-validation.component.html',
    styleUrls: ['./kevlar2-validation.component.scss'],
    standalone: false
})
export class Kevlar2Component implements OnInit {
  isLoading: Boolean;
  storeNumberInput: string;
  availableIndexes = [];
  hasSubmittedStore = false;
  availableIndexesLoaded = false;
  currentStore: string;
  selectedIndexes = [];
  compareResultsReceived = false;
  compareIsRunning = false;
  indexes: {} = {};
  checkNumApsGood = false;
  checkClientTableGood = false;
  index1PortsDict: {} = {};
  index2PortsDict: {} = {};
  clients_in_1_not_2: any[] = [];
  clients_in_2_not_1: any[] = [];
  devices_removed = [];
  devices_added = [];
  devices_swapped_to_bad_port = [];
  allTestsGood = false;
  checkPorts2324Good = false;
  checkSTPBlockingGood = false;
  switchesPort23Offline = [];
  switchesPort24Offline = [];
  switchesPort23NotFound = [];
  switchesPort24NotFound = [];
  switchesMS225 = [];
  switchesMS210 = [];
  switchesMS120 = [];
  countMS225OnlineBeforeInstall = 0;
  countMS210OnlineAfterInstall = 0;
  checkSwitchCountsGood = false;
  checkWan1Good = false;


  @ViewChild('availableIndexesTable') availableIndexesTable: MatTable<AvailableIndexes>;
  @ViewChild('availableIndexesPaginator', { static: true }) paginatorA: MatPaginator;
  displayedColumnsAvailableIndexes = ['store', 'timestamp', 'status', 'action'];
  availableIndexesDataSource = new MatTableDataSource<AvailableIndexes>(this.availableIndexes);

  constructor(
    private kevlar2Service: Kevlar2Service, private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.isLoading = false;
  }

  toInt(str) {
    return parseInt(str)
  }

  async getAvailableKevlar2Indexes() {
    this.isLoading = true;
    this.availableIndexesLoaded = false;
    this.availableIndexes = [];
    this.selectedIndexes = [];
    this.index2PortsDict = {};
    this.index1PortsDict = {};
    this.clients_in_1_not_2 = [];
    this.clients_in_2_not_1 = [];
    this.devices_removed = [];
    this.devices_added = [];
    this.devices_swapped_to_bad_port = [];
    this.switchesPort23Offline = [];
    this.switchesPort24Offline = [];
    this.switchesPort23NotFound = [];
    this.switchesPort24NotFound = [];
    this.switchesMS210 = [];
    this.switchesMS225 = [];
    this.switchesMS120 = [];
    this.countMS225OnlineBeforeInstall = 0;
    this.countMS210OnlineAfterInstall = 0;
    this.compareResultsReceived = false;
    await this.getAvailableIndexes();
    this.isLoading = false;
  }

  async createKevlar2Index() {
    this.isLoading = true;
    this.currentStore = this.storeNumberInput;
    // this.resetView();
    await this.createIndex();
    this.isLoading = false;
  }

  refreshAvailableIndexes() {
    this.getAvailableKevlar2Indexes();
  }

  isStoreNumberInvalid() {

    if (this.storeNumberInput) {
      return this.storeNumberInput.toString().length > 5;
    } else {
      return true;
    }
  }

  // resetView() {
  //
  // }

  async getAvailableIndexes() {
    try {
      const observable = await this.kevlar2Service.getAvailableKevlar2IndexesWithStatus(this.storeNumberInput);
      const response_data: any = await firstValueFrom(observable);
      response_data["message"].forEach(index => {
        this.availableIndexes.push(index);
      });
      // Cheeky way to go from newest to oldest
      this.availableIndexes.reverse()
      if (response_data["status"] == 200) {
        this.availableIndexesDataSource.paginator = this.paginatorA;
        this.availableIndexesDataSource.data = this.availableIndexes;
        this.availableIndexesTable.renderRows();
      } else {
        this.dialog.open(ErrorCardComponent, { data: {
          message: "Unknown error grabbing snapshots"
        }});
      }
    } catch (error) {
      console.log("Snapshots timed out!");
    } finally {
      this.availableIndexesLoaded = true;
    }
  }

  async createIndex() {
    try {
      const observable = await this.kevlar2Service.createKevlar2Index(this.currentStore);
      const response_data: any = await firstValueFrom(observable);
      if (response_data["status"] == 200) {
        this.dialog.open(MessageDialogComponent, { data: {
          title: "Success",
          content: "Snapshot will show up in the list of Available Snapshots.",
          cancelLabel: "OK",
          buttonType: "basic",
          hideConfirmButton: true
        }});
      } else if (response_data["status"] == 500) {
        this.dialog.open(ErrorCardComponent, { data: {
          message: "Error creating snapshot. Please try again or contact owner."
        }});
      } else {
        this.dialog.open(MessageDialogComponent, { data: {
          title: "Notice",
          content: "Server took too long to respond. The snapshot should still show up within 5 minutes.",
          cancelLabel: "OK",
          buttonType: "basic",
          hideConfirmButton: true
        }});
      }
    } catch (error) {
      this.dialog.open(ErrorCardComponent, { data: {
        message: "Error creating the snapshot. If this happens again, contact the owner."
      }});
      console.log("Snapshot creation timed out!");
    } finally {
      this.availableIndexesLoaded = true;
    }
  }

  async getIndex(storeNumber, timestamp, index) {
    try {
      const observable = await this.kevlar2Service.getKevlar2Index(storeNumber, timestamp);
      const response_data: any = await firstValueFrom(observable);
      if (response_data["status"] == 200) {
        this.indexes[index] = response_data["message"]
      } else {
        this.dialog.open(ErrorCardComponent, { data: {
          message: "Unknown error grabbing snapshots"
        }});
      }
    } catch (error) {
      console.log("Snapshots timed out!");
    }
  }

  applyAvailableIndexesFilter(filterValue: string) {
    this.availableIndexesDataSource.filter = filterValue.trim().toLowerCase();
  }

  displayAvailableIndexesTable() {
    if (this.availableIndexesLoaded) {
      return "block";
    } else {
      return "none";
    }
  }

  displayAvailableIndexesHeader() {
    if (this.availableIndexesLoaded) {
      return "flex";
    } else {
      return "none";
    }
  }

  selectIndex(index) {
    if (this.selectedIndexes.length > 1) {
      return;
    }
    this.selectedIndexes.push(index);
  }

  deselect() {
    this.selectedIndexes = [];
    this.index2PortsDict = {};
    this.index1PortsDict = {};
    this.clients_in_1_not_2 = [];
    this.clients_in_2_not_1 = [];
    this.devices_removed = [];
    this.devices_added = [];
    this.devices_swapped_to_bad_port = [];
    this.indexes = {};
    this.compareResultsReceived = false;
    this.switchesPort23Offline = [];
    this.switchesPort24Offline = [];
    this.switchesPort23NotFound = [];
    this.switchesPort24NotFound = [];
    this.switchesMS210 = [];
    this.switchesMS225 = [];
    this.switchesMS120 = [];
    this.countMS225OnlineBeforeInstall = 0;
    this.countMS210OnlineAfterInstall = 0;
  }

  async submitCompare(selectedIndexes) {
    this.compareIsRunning = true;
    // this.resetView();
    await this.getIndex(selectedIndexes[0].store, selectedIndexes[0].timestamp, 0);
    await this.getIndex(selectedIndexes[1].store, selectedIndexes[1].timestamp, 1);
    console.log('INDEX 1: ');
    console.log(this.indexes[0]);
    console.log('INDEX 2: ');
    console.log(this.indexes[1]);
    this.executeCompare();
    this.compareIsRunning = false;
    this.compareResultsReceived = true;
  }

  executeCompare() {
    // console.log("Executing checks...")
    // Compare num APs online
    this.checkNumApsGood = this.checkNumAps(this.indexes[0], this.indexes[1]);
    this.checkClientTableGood = this.checkClientTable(this.indexes[0], this.indexes[1]);
    this.checkPorts2324Good = this.checkPorts2324();
    this.checkSTPBlockingGood = this.checkSTPBlocking();
    this.checkSwitchCountsGood = this.checkSwitchCounts();
    this.checkWan1Good = this.checkWan1();

    this.allTestsGood = this.checkClientTableGood && this.checkNumApsGood && this.checkPorts2324Good && this.checkSTPBlockingGood && this.checkSwitchCountsGood && this.checkWan1Good;

  }

  checkSwitchCounts() {
    // For backwards compatibility; older indexes don't have this key-value
    if (this.indexes[0].switches != undefined && this.indexes[1].switches != undefined) {
      this.switchesMS225 = this.indexes[1].switch_statuses.filter(_switch => _switch.model === "MS225-24P");
      this.switchesMS210 = this.indexes[1].switch_statuses.filter(_switch => _switch.model === "MS210-24P");
      this.switchesMS120 = this.indexes[1].switch_statuses.filter(_switch => _switch.model === "MS120-8LP");

      this.countMS225OnlineBeforeInstall = this.indexes[0].switch_statuses.filter(_switch => _switch.model === "MS225-24P").reduce((total, _switch) => {
        return total + (_switch["status"] == "online" ? 1 : 0)
      }, 0);
      // console.log("SWITCHES MS225 ONLINE BEFORE INSTALL")
      // console.log(this.countMS225OnlineBeforeInstall)

      // console.log("SWITCHES MS210 ONLINE AFTER INSTALL")
      // console.log(this.switchesMS210)
      this.countMS210OnlineAfterInstall = this.switchesMS210.reduce((total, _switch) => {
        return total + (_switch["status"] == "online" ? 1 : 0)
      }, 0);
      // console.log(this.countMS210OnlineAfterInstall)
      // console.log(this.countMS225OnlineBeforeInstall)
    }
    else {
      return false;
    }

    return this.countMS210OnlineAfterInstall >= this.countMS225OnlineBeforeInstall;
  }

  checkWan1() {
    // For backwards compatibility; older indexes don't have this key-value
    if (this.indexes[1].on_backup == undefined) {
      return false;
    }

    return !this.indexes[1].on_backup;
  }

  checkPorts2324() {
    // console.log(this.indexes);

    let portStatuses = this.indexes[1].port_statuses;
    let port2324NotFound = false;
    let portStatusesKeys = Object.keys(portStatuses);
    let testsGood = true;

    portStatusesKeys.forEach(device => {

      // Skip MS225s and MS120s for this check. They should be "off" or "dormant" anyways.
      // let isNotMS210 = this.indexes[1].switch_statuses.filter(_switch => (_switch.model === "MS225-24P" || _switch.model === "MS120-8LP") && _switch.name === device).length > 0;
      let isNotMS210 = this.indexes[1].switch_statuses.filter(_switch => _switch.model !== "MS210-24P" && _switch.name === device).length > 0;
      console.log(`Device "${device}" is not MS210: ${isNotMS210}`)
      if (isNotMS210) {
        return
      }

      let found23 = false;
      let port23Online = false;
      let found24 = false;
      let port24Online = false;
      portStatuses[device].forEach(port => {
        if (port.portId === "23") {
          found23 = true;
          if (port.status === "Connected") {
            port23Online = true;
          } else {
            this.switchesPort23Offline.push(device);
          }
        }
        if (port.portId === "24") {
          found24 = true;
          if (port.status === "Connected") {
            port24Online = true;
          } else {
            this.switchesPort24Offline.push(device);
          }
        }
      });

      if (!found23) {
        this.switchesPort23NotFound.push(device);
      }
      if (!found24) {
        this.switchesPort24NotFound.push(device);
      }

      // If it's false for any switch, keep it false.
      testsGood = testsGood ? [found23, port23Online, found24, port24Online].every(val => val === true) : false;
    });

    return testsGood;
  }

  checkSTPBlocking() {
    return true;
  }

  checkNumAps(index1, index2) {
    return index1["num_aps_online"] == index2["num_aps_online"] ? true : false;
  }

  isSwappablePort(port1, port2) {
    let isSwappable = true;

    // Allow devices to change ports if they remain within their 'group'
    const SWAPPABLE_PORT_RANGES = [
      [1, 12],
      [15, 18]
    ]
    SWAPPABLE_PORT_RANGES.forEach(portRange => {
      // If port1 is in the current portRange
      if (port1 >= portRange[0] && port1 <= portRange[1]) {
        // If port2 is not also in that portRange, then return false
        if (!(port2 >= portRange[0] && port2 <= portRange[1])) {
          isSwappable = false;
        }
      }
    });
    // console.log('Ports ', port1, ' and ', port2, ' can be swapped: ', isSwappable);

    // All other cases, return true
    return isSwappable;
  }

  checkClientTable(index1, index2) {

    // const SWAPPABLE_PORT_RANGES = {
    //   '1-12': 1,
    //   '15-18': 2,
    //   '19-21': 3
    // }

    this.index1PortsDict = {}
    this.index2PortsDict = {}
    this.clients_in_1_not_2 = [];
    this.clients_in_2_not_1 = [];

    // Create dictionaries to easily compare ports from each index
    index1["clients"].forEach(client => {
      if (client["switchport"] in this.index1PortsDict) {
        this.index1PortsDict[client["switchport"]][client["mac"]] = client;
      } else {
        this.index1PortsDict[client["switchport"]] = { [client["mac"]] : client };
      }
    });


    index2["clients"].forEach(client => {
      if (client["switchport"] in this.index2PortsDict) {
        this.index2PortsDict[client["switchport"]][client["mac"]] = client;
      } else {
        this.index2PortsDict[client["switchport"]] = { [client["mac"]] : client };
      }
    });

    console.log("INDEX1 PORTS DICT: ", this.index1PortsDict)
    console.log("INDEX2 PORTS DICT: ", this.index2PortsDict)

    // Find clients in the "before" index but not the "after"
    Object.keys(this.index1PortsDict).forEach(port => {
      Object.keys(this.index1PortsDict[port]).forEach(mac => {
        // If the port is no longer present, or the mac address is no longer present on that port,
        // add it to the list.
        if (!(port in this.index2PortsDict) || !(mac in this.index2PortsDict[port])) {
          this.clients_in_1_not_2.push(this.index1PortsDict[port][mac]);
        }
      });
    });

    // Find clients in the "after" index but not the "before"
    Object.keys(this.index2PortsDict).forEach(port => {
      Object.keys(this.index2PortsDict[port]).forEach(mac => {
        if (!(port in this.index1PortsDict) || !(mac in this.index1PortsDict[port])) {
          this.clients_in_2_not_1.push(this.index2PortsDict[port][mac]);
        }
      });
    });

    // ---------------------------------------------------------
    // The next two loops filter out clients that moved ports WITHIN swappable ranges
    this.devices_removed = [];
    this.devices_added = [];
    this.devices_swapped_to_bad_port = [];

    this.clients_in_1_not_2.forEach(removed_client => {
      let found_in_other_index = false;
      this.clients_in_2_not_1.forEach(added_client => {
        if (added_client["mac"] == removed_client["mac"]) {
          found_in_other_index = true;
          // if (!isSwappablePort(added_client["switchport"], removed_client["switchport"])) {

          // }
        }
      });
      if (!found_in_other_index) {
        this.devices_removed.push(removed_client)
      }
    });

    this.clients_in_2_not_1.forEach(added_client => {
      let found_in_other_index = false;
      this.clients_in_1_not_2.forEach(removed_client => {
        if (removed_client["mac"] == added_client["mac"]) {
          found_in_other_index = true;
          if (!this.isSwappablePort(removed_client["switchport"], added_client["switchport"])) {
            // Adding key-value to dictionary
            let added_client_with_previous_port = added_client;
            added_client_with_previous_port["previousSwitchport"] = removed_client["switchport"];
            this.devices_swapped_to_bad_port.push(added_client_with_previous_port);
          }
        }
      });
      if (!found_in_other_index) {
        this.devices_added.push(added_client)
      }
    });

    // console.log(this.devices_swapped_to_bad_port)


    // console.log("IN FIRST BUT NOT SECOND: ", this.clients_in_1_not_2);
    // console.log("IN SECOND BUT NOT FIRST: ", this.clients_in_2_not_1);
    // console.log("______________________________")

    return this.devices_removed.length == 0 && this.devices_swapped_to_bad_port.length == 0;
  }

}
